import { FC, useEffect } from 'react'
import { useNavigate, useSearchParams, createSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Helmet } from 'react-helmet'

import { APP_ROUTES } from '@/common/constants'
import { APP } from '@/common/strings'
import { useInviteSignupLogic } from './features/invite-signup/useInviteSignupLogic'
import { InviteSignupForm } from './features/invite-signup/components/InviteSignupForm'

/** Production page — wires useInviteSignupLogic result to InviteSignupForm.
 *  Navigation guards are kept here (not in the hook) since they are route concerns, not business logic.
 */
export const InviteSignupPage: FC = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const logic = useInviteSignupLogic()

    // Guard 1: Redirect to generic signup if token is missing
    useEffect(() => {
        const token = searchParams.get('token')
        if (!token) {
            toast.error('No invite token provided')
            navigate(APP_ROUTES.SIGNUP, { replace: true })
        }
    }, [searchParams, navigate])

    // Guard 2: Redirect to generic signup if token is invalid or expired
    useEffect(() => {
        const token = searchParams.get('token')
        if (token && !logic.isValidToken) {
            toast.error('Invalid or expired invite link. Please use the generic signup page.')
            navigate(APP_ROUTES.SIGNUP, { replace: true })
        }
    }, [searchParams, logic.isValidToken, navigate])

    // Handle successful signup navigation
    const handleSubmit = async (data: Parameters<typeof logic.onSubmit>[0]) => {
        try {
            const result = await logic.onSubmit(data)
            if (result?.signUpStep === 'CONFIRM_SIGN_UP') {
                navigate({
                    pathname: APP_ROUTES.CONFIRM_USER,
                    search: createSearchParams({ username: data.email }).toString(),
                })
            }
        } catch {
            // Error already handled by toast in hook
        }
    }

    // Don't render form if token is invalid (will redirect)
    if (!logic.isValidToken) {
        return null
    }

    return (
        <>
            <Helmet>
                <title>Sign Up | {APP.title}</title>
            </Helmet>
            <InviteSignupForm
                email={logic.email ?? undefined}
                signupTerms={logic.signupTerms}
                saving={logic.isRunning}
                onSubmit={handleSubmit}
                onTermsChange={logic.onTermsChange}
            />
        </>
    )
}
